import { type CustomerAddress } from '@utazzo/shared';
import { UzBadge, UzButton, UzConfirmDialog, UzStatusBadge } from '@utazzo/ui';
import { MapPin, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';

import { useRemoveAddress } from '../use-customers';

import { AddressFormDialog } from './AddressFormDialog';

interface AddressesStepProps {
  customerId: string;
  addresses: CustomerAddress[];
}

const kindLabel: Record<string, string> = {
  HOME: 'Home',
  WORK: 'Work',
  BILLING: 'Billing',
  SHIPPING: 'Shipping',
  OTHER: 'Other',
};

/**
 * Step 1 of the customer stepper.
 *
 * Lists the addresses already attached to the customer and provides an
 * "Add address" button that opens a dialog form. Each address can be
 * individually removed via a soft delete (DELETE /customers/:id/addresses/:addressId).
 */
export const AddressesStep = ({ customerId, addresses }: AddressesStepProps): React.JSX.Element => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<CustomerAddress | null>(null);
  const removeAddress = useRemoveAddress();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-zinc-600">
          {addresses.length === 0
            ? 'No addresses yet. Add one below.'
            : `${addresses.length} address${addresses.length === 1 ? '' : 'es'}`}
        </p>
        <UzButton size="sm" onClick={() => setDialogOpen(true)}>
          <Plus className="mr-1.5 h-4 w-4" />
          Add address
        </UzButton>
      </div>

      {addresses.length > 0 && (
        <ul className="space-y-3">
          {addresses.map((addr) => (
            <li
              key={addr.id}
              className="bg-background rounded-lg border p-4"
              aria-label={`${kindLabel[addr.kind] ?? addr.kind} address`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-zinc-400" aria-hidden />
                  <div className="min-w-0 space-y-0.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <UzBadge tone="secondary">{kindLabel[addr.kind] ?? addr.kind}</UzBadge>
                      {addr.isPrimary && <UzStatusBadge status="active" />}
                    </div>
                    <p className="truncate text-sm font-medium text-zinc-900">
                      {[addr.line1, addr.line2].filter(Boolean).join(', ')}
                    </p>
                    <p className="text-xs text-zinc-500">
                      {[
                        addr.cityName ?? addr.city?.name,
                        addr.stateName ?? addr.state?.name,
                        addr.pincode,
                        addr.country.name,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                    {addr.landmark && (
                      <p className="text-xs text-zinc-400">Landmark: {addr.landmark}</p>
                    )}
                  </div>
                </div>
                <UzButton
                  size="sm"
                  variant="ghost"
                  onClick={() => setRemoveTarget(addr)}
                  aria-label="Remove address"
                >
                  <Trash2 className="h-4 w-4 text-zinc-400" />
                </UzButton>
              </div>
            </li>
          ))}
        </ul>
      )}

      <AddressFormDialog open={dialogOpen} onOpenChange={setDialogOpen} customerId={customerId} />

      <UzConfirmDialog
        open={removeTarget !== null}
        onOpenChange={(open) => !open && setRemoveTarget(null)}
        title="Remove this address?"
        description={
          removeTarget
            ? `The ${kindLabel[removeTarget.kind] ?? removeTarget.kind} address at ${removeTarget.line1} will be removed.`
            : ''
        }
        confirmLabel="Remove"
        tone="destructive"
        onConfirm={async () => {
          if (removeTarget) {
            await removeAddress.mutateAsync({ customerId, addressId: removeTarget.id });
            setRemoveTarget(null);
          }
        }}
      />
    </div>
  );
};
